import { useState } from 'react';
import { TextField, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { SkillContainer } from './styles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ConfirmActionDialog } from '../../../../Common/ConfirmActionDialog/ConfirmActionDialog';
import { deleteSkill, editSkill } from '../../../../../Services/Skills';

interface Props {
  skill?: Skill;
  skills: Skill[];
  onSuccessEdit?: () => void;
  preventEdit?: boolean;
}

export const Skill = ({ skill, skills, onSuccessEdit, preventEdit }: Props) => {
  
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(skill?.name ?? '');
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [error, setError] = useState(false);
  const queryClient = useQueryClient();

  const errorMessage = t('toast.errors.something_went_wrong');
  const deleteSuccessMessage = t('toast.success.delete', { elem: 'Skill' });
  const editSuccessMessage = t('toast.success.edit', { elem: 'Skill' });
  const confirmDelete = t('skills.delete.confirmation');

  const { mutate: deleteSkillMutation } = useMutation({
    mutationFn: () => deleteSkill({ id: skill!.id! }),
    onSuccess: () => {
      toast.success(deleteSuccessMessage);
      setShowConfirmDeleteDialog(false);
      queryClient.invalidateQueries({ queryKey: ['getSkills'] });
      if (onSuccessEdit) onSuccessEdit();
    },
    onError: () => {
      toast.error(errorMessage);
    },
  });

  const { mutate: editSkillMutation } = useMutation({
    mutationFn: () => editSkill({ id: skill!.id!, name }),
    onSuccess: () => {
      toast.success(editSuccessMessage);
      queryClient.invalidateQueries({ queryKey: ['getSkills'] });
      setIsEditing(false);
      if (onSuccessEdit) onSuccessEdit();
    },
    onError: () => {
      toast.error(errorMessage);
    },
  });

  const handleEdit = () => setIsEditing(true);
  const handleSave = () => editSkillMutation();
  const handleClose = () => {
    setIsEditing(false);
    setName(skill?.name ?? '');
  };

  const isDisabled = skill?.canBeModified === false;
  const disabledTooltip = t('skills.disabled_tooltip');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setName(newValue);

    const isDuplicate = skills.some(
      (s) => s.name.toLowerCase() === newValue.toLowerCase() && s.id !== skill?.id
    );
    setError(isDuplicate);
  };

  return (
    <SkillContainer>
      <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
        {isEditing ? (
          <TextField
            value={name}
            onChange={handleInputChange}
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            autoFocus
            error={error}
            helperText={error ? t('skills.already_exists') : ''}
          />
        ) : (
          <div style={{ flex: 1 }}>{name}</div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginLeft: 5 }}>
          {isEditing ? (
            <>
              <IconButton style={{ marginTop: 0 }} color="primary" onClick={handleSave} disabled={error}>
                <CheckIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton style={{ marginTop: 0 }} color="primary" onClick={handleClose}>
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </>
          ) : (
            <>
              {!preventEdit && (
                <Tooltip title={isDisabled ? disabledTooltip : ''}>
                  <span>
                    <IconButton
                      style={{
                        marginTop: 0,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                      }}
                      color="primary"
                      onClick={handleEdit}
                      disabled={isDisabled}
                    >
                      <EditIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!preventEdit && (
                <Tooltip title={isDisabled ? disabledTooltip : ''}>
                  <span>
                    <IconButton
                      style={{
                        marginTop: 0,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                      }}
                      color="primary"
                      onClick={() => setShowConfirmDeleteDialog(true)}
                      disabled={isDisabled}
                    >
                      <DeleteIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>
      <ConfirmActionDialog
        open={showConfirmDeleteDialog}
        handleClose={() => setShowConfirmDeleteDialog(false)}
        handleConfirm={deleteSkillMutation}
        description={confirmDelete}
      />
    </SkillContainer>
  );
};
